import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, User, Phone, Mail, Calendar, Droplet, MapPin, AlertCircle,
  ClipboardList, FlaskConical, Receipt, FileText, Eye, Clock, Stethoscope,
  CheckCircle, XCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function PatientDetails() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [medicalRecords, setMedicalRecords] = useState([]);
  const [billing, setBilling] = useState([]);
  const [labEntries, setLabEntries] = useState([]);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPatientData();
  }, [patientId]);

  const fetchPatientData = async () => {
    try {
      const [patientRes, recordsRes, billingRes, labRes, visitsRes] = await Promise.all([
        api.get(`/patients/${patientId}`),
        api.get(`/medical-records/patient/${patientId}`),
        api.get(`/billing/patient/${patientId}`),
        api.get(`/lab-entries/patient/${patientId}`),
        api.get(`/visits?patient_id=${patientId}`)
      ]);
      setPatient(patientRes.data);
      setMedicalRecords(recordsRes.data);
      setBilling(billingRes.data);
      setLabEntries(labRes.data);
      setVisits(visitsRes.data);
    } catch (error) {
      toast.error('Failed to fetch patient data');
    } finally {
      setLoading(false);
    }
  };

  const getVisitStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-emerald-100 text-emerald-700';
      case 'in_consultation': return 'bg-purple-100 text-purple-700';
      case 'waiting': return 'bg-amber-100 text-amber-700';
      case 'cancelled': return 'bg-rose-100 text-rose-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getVisitTypeColor = (type) => {
    switch (type) {
      case 'OPD': return 'bg-purple-200 text-blue-700';
      case 'IPD': return 'bg-purple-100 text-purple-700';
      case 'Emergency': return 'bg-rose-100 text-rose-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A238F]"></div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Patient not found</p>
      </div>
    );
  }

  return (
    <div data-testid="patient-details-page">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/patients')}
            data-testid="back-to-patients-button"
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Patients
          </Button>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
            {patient.full_name}
          </h1>
          <p className="text-slate-600 mt-2">{patient.patient_number}</p>
        </div>

        {/* Patient Info Card */}
        <Card className="border border-slate-200 shadow-sm mb-6">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="flex items-start gap-3">
                <Phone className="h-5 w-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-600">Phone</p>
                  <p className="font-medium text-slate-900">{patient.phone}</p>
                </div>
              </div>
              {patient.email && (
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-slate-600">Email</p>
                    <p className="font-medium text-slate-900">{patient.email}</p>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-600">Date of Birth</p>
                  <p className="font-medium text-slate-900">{patient.date_of_birth}</p>
                </div>
              </div>
              {patient.blood_group && (
                <div className="flex items-start gap-3">
                  <Droplet className="h-5 w-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-slate-600">Blood Group</p>
                    <p className="font-medium text-slate-900">{patient.blood_group}</p>
                  </div>
                </div>
              )}
            </div>
            {patient.address && (
              <div className="flex items-start gap-3 mt-6">
                <MapPin className="h-5 w-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-600">Address</p>
                  <p className="font-medium text-slate-900">{patient.address}</p>
                </div>
              </div>
            )}
            {(patient.emergency_contact_name || patient.emergency_contact_phone) && (
              <div className="flex items-start gap-3 mt-6 p-4 bg-amber-50 border border-amber-200 rounded-md">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-900">Emergency Contact</p>
                  <p className="text-sm text-amber-700 mt-1">
                    {patient.emergency_contact_name} - {patient.emergency_contact_phone}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="visits" className="space-y-6">
          <TabsList className="bg-slate-200 p-1">
            <TabsTrigger value="visits" className="text-slate-700 data-[state=active]:bg-white data-[state=active]:text-[#4A238F] data-[state=active]:shadow-sm" data-testid="visits-tab">
              <ClipboardList className="h-4 w-4 mr-2" />
              Visits ({visits.length})
            </TabsTrigger>
            <TabsTrigger value="medical-records" className="text-slate-700 data-[state=active]:bg-white data-[state=active]:text-[#4A238F] data-[state=active]:shadow-sm" data-testid="medical-records-tab">Medical Records</TabsTrigger>
            <TabsTrigger value="lab-results" className="text-slate-700 data-[state=active]:bg-white data-[state=active]:text-[#4A238F] data-[state=active]:shadow-sm" data-testid="lab-results-tab">Lab Results</TabsTrigger>
            <TabsTrigger value="billing" className="text-slate-700 data-[state=active]:bg-white data-[state=active]:text-[#4A238F] data-[state=active]:shadow-sm" data-testid="billing-tab">Billing</TabsTrigger>
          </TabsList>

          {/* Visits Tab */}
          <TabsContent value="visits">
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Visit History
                </CardTitle>
                <Button
                  size="sm"
                  onClick={() => navigate('/visits')}
                  className="bg-[#4A238F] hover:bg-[#3A1B72] text-white"
                >
                  <ClipboardList className="mr-2 h-4 w-4" />
                  New Visit
                </Button>
              </CardHeader>
              <CardContent>
                {visits.length === 0 ? (
                  <div className="text-center py-8">
                    <ClipboardList className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No visit history found</p>
                    <p className="text-sm text-slate-400 mt-1">Check-in this patient to create their first visit</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {visits.map((visit) => (
                      <div 
                        key={visit.id} 
                        className="p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors cursor-pointer"
                        onClick={() => navigate(`/visits/${visit.id}`)}
                        data-testid={`visit-card-${visit.id}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                              {visit.visit_type === 'Emergency' ? (
                                <AlertCircle className="h-5 w-5 text-rose-600" />
                              ) : visit.visit_type === 'IPD' ? (
                                <User className="h-5 w-5 text-purple-600" />
                              ) : (
                                <Stethoscope className="h-5 w-5 text-purple-600" />
                              )}
                            </div>
                            <div>
                              <p className="font-semibold text-slate-900">{visit.visit_number}</p>
                              <p className="text-sm text-slate-600">{visit.chief_complaint}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={getVisitTypeColor(visit.visit_type)}>{visit.visit_type}</Badge>
                            <Badge className={getVisitStatusColor(visit.status)}>
                              {visit.status === 'in_consultation' ? 'In Consultation' : visit.status}
                            </Badge>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                          <div>
                            <p className="text-slate-500">Check-in</p>
                            <p className="font-medium">{formatDateTime(visit.check_in_time)}</p>
                          </div>
                          {visit.consultation_start_time && (
                            <div>
                              <p className="text-slate-500">Consultation</p>
                              <p className="font-medium">{formatDateTime(visit.consultation_start_time)}</p>
                            </div>
                          )}
                          {visit.diagnosis && (
                            <div className="col-span-2">
                              <p className="text-slate-500">Diagnosis</p>
                              <p className="font-medium truncate">{visit.diagnosis}</p>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200">
                          <div className="flex items-center gap-4 text-xs text-slate-500">
                            {visit.lab_count > 0 && (
                              <span className="flex items-center gap-1">
                                <FlaskConical className="h-3 w-3" />
                                {visit.lab_count} lab(s)
                              </span>
                            )}
                            {visit.prescription_count > 0 && (
                              <span className="flex items-center gap-1">
                                <FileText className="h-3 w-3" />
                                {visit.prescription_count} Rx
                              </span>
                            )}
                            {visit.billing_total > 0 && (
                              <span className="flex items-center gap-1">
                                <Receipt className="h-3 w-3" />
                                ${visit.billing_total.toFixed(2)}
                              </span>
                            )}
                          </div>
                          <Button size="sm" variant="ghost" className="text-purple-600">
                            <Eye className="h-4 w-4 mr-1" />
                            View Details
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="medical-records">
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Medical History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {medicalRecords.length === 0 ? (
                  <p className="text-slate-500 text-sm">No medical records found</p>
                ) : (
                  <div className="space-y-4">
                    {medicalRecords.map((record) => (
                      <div key={record.id} className="p-4 bg-slate-50 rounded-md border border-slate-200">
                        <p className="font-medium text-slate-900">{record.diagnosis}</p>
                        {record.symptoms && (
                          <p className="text-sm text-slate-600 mt-2"><strong>Symptoms:</strong> {record.symptoms}</p>
                        )}
                        {record.prescription && (
                          <p className="text-sm text-slate-600 mt-2"><strong>Prescription:</strong> {record.prescription}</p>
                        )}
                        {record.notes && (
                          <p className="text-sm text-slate-600 mt-2"><strong>Notes:</strong> {record.notes}</p>
                        )}
                        <p className="text-xs text-slate-500 mt-3">{new Date(record.created_at).toLocaleDateString()}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="lab-results">
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Laboratory Results
                </CardTitle>
              </CardHeader>
              <CardContent>
                {labEntries.length === 0 ? (
                  <p className="text-slate-500 text-sm">No lab results found</p>
                ) : (
                  <div className="space-y-4">
                    {labEntries.map((lab) => (
                      <div key={lab.id} className="p-4 bg-slate-50 rounded-md border border-slate-200">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-medium text-slate-900">{lab.test_name}</p>
                            <p className="text-sm text-slate-600 mt-1">{lab.test_category}</p>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded ${
                            lab.status === 'verified' ? 'bg-emerald-100 text-emerald-700' :
                            lab.status === 'completed' ? 'bg-purple-200 text-blue-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {lab.status}
                          </span>
                        </div>
                        {lab.results && Object.keys(lab.results).length > 0 && (
                          <div className="mt-3 space-y-1">
                            <p className="text-sm font-medium text-slate-700">Results:</p>
                            {Object.entries(lab.results).map(([key, value]) => (
                              <p key={key} className="text-sm text-slate-600">
                                <span className="font-medium">{key}:</span> {value}
                              </p>
                            ))}
                          </div>
                        )}
                        {lab.interpretation && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-slate-700">Interpretation:</p>
                            <p className="text-sm text-slate-600">{lab.interpretation}</p>
                          </div>
                        )}
                        <p className="text-xs text-slate-500 mt-3">Ordered: {new Date(lab.ordered_date).toLocaleDateString()}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="billing">
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Billing Records
                </CardTitle>
              </CardHeader>
              <CardContent>
                {billing.length === 0 ? (
                  <p className="text-slate-500 text-sm">No billing records found</p>
                ) : (
                  <div className="space-y-4">
                    {billing.map((bill) => (
                      <div key={bill.id} className="p-4 bg-slate-50 rounded-md border border-slate-200">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-slate-900">{bill.invoice_number}</p>
                            <p className="text-sm text-slate-600 mt-1">Total: ${bill.total_amount.toFixed(2)}</p>
                            <p className="text-sm text-slate-600">Paid: ${bill.paid_amount.toFixed(2)}</p>
                            <p className="text-sm text-slate-600">Balance: ${bill.balance.toFixed(2)}</p>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded ${
                            bill.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                            bill.payment_status === 'partial' ? 'bg-amber-100 text-amber-700' :
                            'bg-rose-100 text-rose-700'
                          }`}>
                            {bill.payment_status}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-3">{new Date(bill.created_at).toLocaleDateString()}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </motion.div>
    </div>
  );
}